// v4 ore-rock — REDESIGNED. A STRATIFIED ORE BODY — a stepped stack of
// angular slate-grey slabs breaking out of the ground at a tilt, with
// rust-iron seams clamped between the strata and metallic nodes studding the
// exposed faces, ringed by dark scree. Each variant re-tilts the strata and
// re-seats the nodes.
//
// Ported 1:1 from age-of-mcp's v4/ore-rock.ts. The v4 `scree` helper (with
// its `h01`/`lerp`) is inlined locally because v4's variant differs from the
// library's `mineral.scree`.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

function h01(seed: number, n: number): number {
  const x = Math.sin(seed * 117.32 + n * 271.7 + 31.4) * 43758.5453;
  return x - Math.floor(x);
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function scree(
  seed: number, count: number,
  dist: readonly [number, number], size: readonly [number, number],
  colors: readonly string[],
): Part[] {
  const parts: Part[] = [];
  for (let i = 0; i < count; i++) {
    const ang = (i / count) * Math.PI * 2 + h01(seed, i * 4) * 1.6;
    const d = lerp(dist[0], dist[1], h01(seed, i * 4 + 1));
    const r = lerp(size[0], size[1], h01(seed, i * 4 + 2));
    const shard = i % 2 === 1;
    parts.push({
      geometry: shard
        ? new THREE.TetrahedronGeometry(r * 0.95, 0)
        : new THREE.IcosahedronGeometry(r, 0),
      color: colors[i % colors.length]!,
      position: [Math.cos(ang) * d, shard ? r * 0.28 : r * 0.5, Math.sin(ang) * d],
      rotation: [
        h01(seed, i * 4 + 3) * 0.9,
        h01(seed, i * 7 + 11) * Math.PI * 2,
        h01(seed, i * 5 + 23) * 0.9,
      ],
      scale: [1.05, 0.92, 1.0],
    });
  }
  return parts;
}

const HOST  = "#565a63";   // dark slate host rock
const HOST2 = "#62666f";
const SEAM  = "#8a4f33";   // rust-iron seam between strata
const NODE  = "#3d424a";   // near-black metallic node
const GLINT = "#9aa3ad";   // cold metallic glint facet

function partsFor(v: number): Part[] {
  const seed = v * 11 + 3;
  const yaw  = v * 1.9;
  const tilt = lerp(0.08, 0.18, h01(seed, 0));
  const parts: Part[] = [];

  // Three stepped strata slabs, each narrower and turned a little further,
  // with a thin rust seam squeezed between each pair.
  const widths  = [0.5, 0.4, 0.3];
  const heights = [0.16, 0.14, 0.12];
  let y = 0;
  for (let i = 0; i < 3; i++) {
    const w = widths[i]!, hh = heights[i]!;
    parts.push({
      geometry: new THREE.BoxGeometry(w, hh, w * 0.82),
      color: i % 2 === 0 ? HOST : HOST2,
      position: [lerp(-0.04, 0.04, h01(seed, i * 3 + 1)), y + hh / 2, 0],
      rotation: [tilt * (i === 1 ? -0.6 : 1), yaw + i * 0.32, tilt * 0.4],
    });
    if (i < 2) {
      parts.push({
        geometry: new THREE.BoxGeometry(w * 0.8, 0.028, w * 0.66),
        color: SEAM,
        position: [0, y + hh + 0.004, 0],
        rotation: [tilt * (i === 0 ? 1 : -0.6), yaw + i * 0.5, tilt * 0.4],
      });
    }
    y += hh - 0.015;                       // strata sit pressed into each other
  }

  // Metallic ore nodes studding the exposed faces — dark lumps with one
  // brighter glint shard so the metal catches the key light.
  for (let i = 0; i < 4; i++) {
    const ang = yaw + i * 1.7 + h01(seed, 20 + i) * 0.8;
    const d = lerp(0.12, 0.19, h01(seed, 30 + i));
    const r = lerp(0.05, 0.085, h01(seed, 40 + i));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(r, 0),
      color: i === 1 ? GLINT : NODE,
      position: [Math.cos(ang) * d, lerp(0.1, 0.34, h01(seed, 50 + i)), Math.sin(ang) * d],
      rotation: [0, ang, h01(seed, 60 + i) * 0.7],
    });
  }

  parts.push(...scree(seed, 3, [0.13, 0.19], [0.035, 0.055], [HOST, NODE, HOST2]));
  return parts;
}

export const oreRockV4 = defineModel({
  id: "ore-rock-v4",
  batchId: "polished",
  name: "Ore rock (v4)",
  tags: ["scenery","ore","rock","low-poly","age-of-mcp","fable"],
  summary: "Low-poly ore rock.",
  description: "Ported from the age-of-mcp gallery, v4 style.",
  author: "age-of-mcp",
  footprint: 0.46,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => partsFor(0) },
    { id: "variant-1", label: "Variant 2", parts: () => partsFor(1) },
    { id: "variant-2", label: "Variant 3", parts: () => partsFor(2) },
  ],
  animations: [],
});
